import { c as _c } from "react/compiler-runtime";
import { useToast } from '@7f-ui/components/toast/toast-provider';
import type { CartItemExperienceUpsertInput } from '@data-access/graphql/schema.types';
import { CartDocument, CartItemAccommodationsCreateDocument, CartItemAccommodationUpsertDocument, CartItemBeachChairInsertDocument, CartItemDeleteDocument, CartItemExperienceUpsertDocument, CartItemRentableUpsertDocument } from '@data-access/lib/gql/cart/documents';
import { getGraphqlQueryKey, useGraphQLMutation } from '@data-access/lib/graphql-query';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { isCartCurrencyMismatchError } from '../cart-error-codes';
type InputExperienceData = {
  experienceId: CartItemExperienceUpsertInput['experienceId'];
  timeslotId?: CartItemExperienceUpsertInput['timeslotId'];
  visitDate?: CartItemExperienceUpsertInput['visitDate'];
  selectedOptions?: Record<string, number>;
};
type UpsertOptionsExperience = Parameters<typeof useGraphQLMutation<ResultOf<typeof CartItemExperienceUpsertDocument>, VariablesOf<typeof CartItemExperienceUpsertDocument>>>[1];
type DeleteOptions = Parameters<typeof useGraphQLMutation<ResultOf<typeof CartItemDeleteDocument>, VariablesOf<typeof CartItemDeleteDocument>>>[1];
export const useCartItem = () => {
  const $ = _c(55);
  const queryClient = useQueryClient();
  const toast = useToast();
  let t0;
  if ($[0] !== toast) {
    t0 = error => {
      if (!isCartCurrencyMismatchError(error)) {
        return;
      }
      toast({
        type: "error",
        message: "In deinem Warenkorb liegt bereits etwas in einer anderen W\xE4hrung. L\xF6sche deinen Warenkorb, um dieses Produkt hinzuf\xFCgen zu k\xF6nnen."
      });
    };
    $[0] = toast;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const toastCurrencyMismatch = t0;
  let t1;
  if ($[2] !== queryClient) {
    t1 = () => {
      queryClient.resetQueries({
        queryKey: getGraphqlQueryKey(CartDocument)
      });
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1 || $[5] !== toastCurrencyMismatch) {
    t2 = {
      onSuccess: t1,
      onError: toastCurrencyMismatch
    };
    $[4] = t1;
    $[5] = toastCurrencyMismatch;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    mutate: addCartItemExperience,
    mutateAsync: addCartItemExperienceAsync
  } = useGraphQLMutation(CartItemExperienceUpsertDocument, t2);
  let t3;
  if ($[7] !== queryClient) {
    t3 = {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGraphqlQueryKey(CartDocument)
        });
      }
    };
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    mutate: updateCartItemExperience
  } = useGraphQLMutation(CartItemExperienceUpsertDocument, t3);
  const {
    mutate: removeCartItem
  } = useGraphQLMutation(CartItemDeleteDocument);
  let t4;
  if ($[9] !== queryClient) {
    t4 = () => {
      queryClient.resetQueries({
        queryKey: getGraphqlQueryKey(CartDocument)
      });
    };
    $[9] = queryClient;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4 || $[12] !== toastCurrencyMismatch) {
    t5 = {
      onSuccess: t4,
      onError: toastCurrencyMismatch
    };
    $[11] = t4;
    $[12] = toastCurrencyMismatch;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    mutate: addAccommodation,
    mutateAsync: addAccommodationAsync
  } = useGraphQLMutation(CartItemAccommodationUpsertDocument, t5);
  let t6;
  if ($[14] !== queryClient) {
    t6 = () => {
      queryClient.resetQueries({
        queryKey: getGraphqlQueryKey(CartDocument)
      });
    };
    $[14] = queryClient;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6 || $[17] !== toastCurrencyMismatch) {
    t7 = {
      onSuccess: t6,
      onError: toastCurrencyMismatch
    };
    $[16] = t6;
    $[17] = toastCurrencyMismatch;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    mutate: createAccommodations,
    mutateAsync: createAccommodationsAsync
  } = useGraphQLMutation(CartItemAccommodationsCreateDocument, t7);
  let t8;
  if ($[19] !== queryClient) {
    t8 = () => {
      queryClient.invalidateQueries({
        queryKey: getGraphqlQueryKey(CartDocument)
      });
    };
    $[19] = queryClient;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8 || $[22] !== toastCurrencyMismatch) {
    t9 = {
      onSuccess: t8,
      onError: toastCurrencyMismatch
    };
    $[21] = t8;
    $[22] = toastCurrencyMismatch;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const {
    mutate: updateAccommodation
  } = useGraphQLMutation(CartItemAccommodationUpsertDocument, t9);
  let t10;
  if ($[24] !== queryClient) {
    t10 = () => {
      queryClient.resetQueries({
        queryKey: getGraphqlQueryKey(CartDocument)
      });
    };
    $[24] = queryClient;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== toastCurrencyMismatch) {
    t11 = {
      onSuccess: t10,
      onError: toastCurrencyMismatch
    };
    $[26] = t10;
    $[27] = toastCurrencyMismatch;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const {
    mutate: addBeachChair
  } = useGraphQLMutation(CartItemBeachChairInsertDocument, t11);
  let t12;
  if ($[29] !== queryClient) {
    t12 = () => {
      queryClient.resetQueries({
        queryKey: getGraphqlQueryKey(CartDocument)
      });
    };
    $[29] = queryClient;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== toastCurrencyMismatch) {
    t13 = {
      onSuccess: t12,
      onError: toastCurrencyMismatch
    };
    $[31] = t12;
    $[32] = toastCurrencyMismatch;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const {
    mutate: upsertRentable
  } = useGraphQLMutation(CartItemRentableUpsertDocument, t13);
  let t14;
  if ($[34] !== addCartItemExperience) {
    t14 = (input, options) => {
      addCartItemExperience(mapToExperienceInput(input), options);
    };
    $[34] = addCartItemExperience;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const addExperience = t14;
  let t15;
  if ($[36] !== addCartItemExperienceAsync) {
    t15 = (input_0, options_0) => addCartItemExperienceAsync(mapToExperienceInput(input_0), options_0);
    $[36] = addCartItemExperienceAsync;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const addExperienceAsync = t15;
  let t16;
  if ($[38] !== updateCartItemExperience) {
    t16 = (itemId, input_1, options_1) => {
      updateCartItemExperience({
        itemId,
        ...mapToExperienceInput(input_1)
      }, options_1);
    };
    $[38] = updateCartItemExperience;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const updateExperience = t16;
  let t17;
  if ($[40] !== queryClient || $[41] !== removeCartItem) {
    t17 = (id, options_2) => {
      removeCartItem({
        id
      }, {
        ...options_2,
        onSuccess: async (...t18) => {
          const args = t18;
          await queryClient.invalidateQueries({
            queryKey: getGraphqlQueryKey(CartDocument)
          });
          options_2?.onSuccess?.(...args);
        }
      });
    };
    $[40] = queryClient;
    $[41] = removeCartItem;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  const remove = t17;
  let t18;
  if ($[43] !== addAccommodation || $[44] !== addAccommodationAsync || $[45] !== addBeachChair || $[46] !== addExperience || $[47] !== addExperienceAsync || $[48] !== createAccommodations || $[49] !== createAccommodationsAsync || $[50] !== remove || $[51] !== updateAccommodation || $[52] !== updateExperience || $[53] !== upsertRentable) {
    t18 = {
      addExperience,
      addExperienceAsync,
      updateExperience,
      addAccommodation,
      addAccommodationAsync,
      updateAccommodation,
      createAccommodations,
      createAccommodationsAsync,
      addBeachChair,
      upsertRentable,
      remove
    };
    $[43] = addAccommodation;
    $[44] = addAccommodationAsync;
    $[45] = addBeachChair;
    $[46] = addExperience;
    $[47] = addExperienceAsync;
    $[48] = createAccommodations;
    $[49] = createAccommodationsAsync;
    $[50] = remove;
    $[51] = updateAccommodation;
    $[52] = updateExperience;
    $[53] = upsertRentable;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  return t18;
};
const mapToExperienceInput = ({
  experienceId,
  timeslotId,
  visitDate,
  selectedOptions
}: InputExperienceData) => ({
  input: {
    experienceId,
    timeslotId: timeslotId ?? null,
    visitDate: visitDate ?? null,
    tickets: Object.entries(selectedOptions ?? {}).filter(([, count]) => count > 0).map(([ticketId, count]) => ({
      ticketId,
      count
    }))
  }
});